<template>
    <div class="table-container mb-2" ref="tableContainer">
        <table
            class="table table-light table-bordered no-header-border fixed-table"
        >
            <colgroup>
                <!-- Define fixed column widths -->
                <col span="2" class="fixed-col-150" />
                <col
                    v-for="datetime in datetimes"
                    :key="String(datetime.oriDate)"
                    class="fixed-col-70"
                />
            </colgroup>
            <thead>
                <tr class="fixed-row">
                    <th
                        scope="col"
                        colspan="2"
                        class="border-secondary fixed-col-150"
                        style="white-space: break-spaces;"
                    ></th>

                    <th
                        v-for="datetime in datetimes"
                        :key="String(datetime.oriDate)"
                        class="border-secondary text-center"
                        :style="{
                            color: datetime.isCurrentTime ? 'red' : null
                        }"
                    >
                        <div
                            class="h-100 d-flex flex-column justify-content-center"
                        >
                            <span>{{
                                datetime.dateSimplify.toUpperCase()
                            }}</span>
                            <span
                                >{{ datetime.hour }}
                                {{ datetime.meridiem }}</span
                            >
                        </div>
                    </th>
                </tr>
            </thead>
            <tbody>
                <!-- vital section start -->
                <tr class="fixed-row invi-border-row">
                    <th scope="row" :colspan="2" class="fixed-col-150">
                        Vitals
                        <span v-if="activities.Vitals.length == 0"
                            >- No activities</span
                        >
                    </th>
                    <td
                        v-for="(datetime, hour) in datetimes"
                        :key="hour"
                        scope="rowgroup"
                        class="p-0 height-one"
                    >
                        <div
                            v-if="datetime.isCurrentTime"
                            :style="minuteLineStyle"
                            v-b-tooltip.hover.left="currentLineTooltip"
                        >
                            <!-- This div represents the line -->
                        </div>
                    </td>
                </tr>
                <template v-if="activities.Vitals.length > 0">
                    <tr
                        v-for="(activity, index) in activities.Vitals"
                        :key="activity.id"
                        class="fixed-row"
                    >
                        <th
                            scope="row"
                            colspan="2"
                            class="border-dark p-0 fixed-col-150 align-top"
                        >
                            <TreatmentActivityDetails
                                :key="index"
                                :activity="prepareActivity(activity)"
                                :treatment="treatment"
                                :prepareDateTime="prepareDateTime"
                                :dialogs="dialogs"
                                treatmentBoardView="patient"
                                :activityType="TREATMENT_TYPE_STR.VITAL"
                            ></TreatmentActivityDetails>
                        </th>

                        <td
                            v-for="(datetime, hour) in datetimes"
                            :key="hour"
                            scope="rowgroup"
                            class="border-secondary p-0 height-one"
                        >
                            <div
                                v-if="datetime.isCurrentTime"
                                :style="minuteLineStyle"
                                v-b-tooltip.hover.left="currentLineTooltip"
                            >
                                <!-- This div represents the line -->
                            </div>
                            <TreatmentPatientTimelinedDetails
                                :id="`${activity.id}_${hour}`"
                                :ref="`${activity.id}_${hour}`"
                                :treatment="treatment"
                                :activity="
                                    isActivityBelongsHere(activity, hour)
                                "
                                :cellId="`${activity.id}_${hour}`"
                                :cellDate="datetime"
                                :dialogs="dialogs"
                                :parentRef="$refs.tableContainer"
                                :prepareDateTime="prepareDateTime"
                            ></TreatmentPatientTimelinedDetails>
                        </td>
                    </tr>
                </template>
                <!-- medical section end -->

                <!-- medical section start -->
                <tr class="fixed-row invi-border-row">
                    <th
                        scope="row"
                        :colspan="2"
                        class="fixed-col-150 border-right-none"
                    >
                        Medical
                        <span v-if="activities.Medical.length == 0"
                            >- No activities</span
                        >
                    </th>
                    <td
                        v-for="(datetime, hour) in datetimes"
                        :key="hour"
                        scope="rowgroup"
                        class="p-0 height-one"
                    >
                        <div
                            v-if="datetime.isCurrentTime"
                            :style="minuteLineStyle"
                            v-b-tooltip.hover.left="currentLineTooltip"
                        >
                            <!-- This div represents the line -->
                        </div>
                    </td>
                </tr>
                <template v-if="activities.Medical.length > 0">
                    <tr
                        v-for="(activity, index) in activities.Medical"
                        :key="activity.id"
                        class="fixed-row"
                    >
                        <th
                            scope="row"
                            colspan="2"
                            class="border-dark p-0 fixed-col-150 align-top"
                        >
                            <TreatmentActivityDetails
                                :key="index"
                                :activity="prepareActivity(activity)"
                                :treatment="treatment"
                                :prepareDateTime="prepareDateTime"
                                :dialogs="dialogs"
                                treatmentBoardView="patient"
                                :activityType="TREATMENT_TYPE_STR.MEDICAL"
                                :routeOptions="routeOptions"
                                :locationOptions="locationOptions"
                            ></TreatmentActivityDetails>
                        </th>

                        <td
                            v-for="(datetime, hour) in datetimes"
                            :key="hour"
                            scope="rowgroup"
                            class="border-secondary p-0 height-one"
                        >
                            <div
                                v-if="datetime.isCurrentTime"
                                :style="minuteLineStyle"
                                v-b-tooltip.hover.left="currentLineTooltip"
                            >
                                <!-- This div represents the line -->
                            </div>
                            <TreatmentPatientTimelinedDetails
                                :id="`${activity.id}_${hour}`"
                                :ref="`${activity.id}_${hour}`"
                                :treatment="treatment"
                                :activity="
                                    isActivityBelongsHere(activity, hour)
                                "
                                :cellId="`${activity.id}_${hour}`"
                                :cellDate="datetime"
                                :dialogs="dialogs"
                                :parentRef="$refs.tableContainer"
                                :prepareDateTime="prepareDateTime"
                            ></TreatmentPatientTimelinedDetails>
                        </td>
                    </tr>
                </template>
                <!-- vital section end -->

                <!-- other section start -->
                <tr class="fixed-row invi-border-row">
                    <th
                        scope="row"
                        :colspan="2"
                        class="fixed-col-150 border-right-none"
                    >
                        Other
                        <span v-if="activities.Other.length == 0"
                            >- No activities</span
                        >
                    </th>
                    <td
                        v-for="(datetime, hour) in datetimes"
                        :key="hour"
                        scope="rowgroup"
                        class="p-0 height-one"
                    >
                        <div
                            v-if="datetime.isCurrentTime"
                            :style="minuteLineStyle"
                            v-b-tooltip.hover.left="currentLineTooltip"
                        >
                            <!-- This div represents the line -->
                        </div>
                    </td>
                </tr>
                <template v-if="activities.Other.length > 0">
                    <tr
                        v-for="(activity, index) in activities.Other"
                        :key="activity.id"
                        class="fixed-row"
                    >
                        <th
                            scope="row"
                            colspan="2"
                            class="border-dark p-0 fixed-col-150 align-top"
                        >
                            <TreatmentActivityDetails
                                :key="index"
                                :activity="prepareActivity(activity)"
                                :treatment="treatment"
                                :prepareDateTime="prepareDateTime"
                                :dialogs="dialogs"
                                treatmentBoardView="patient"
                                :activityType="TREATMENT_TYPE_STR.OTHER"
                                :labelOptions="labelOptions"
                            ></TreatmentActivityDetails>
                        </th>

                        <td
                            v-for="(datetime, hour) in datetimes"
                            :key="hour"
                            scope="rowgroup"
                            class="border-secondary p-0 height-one"
                        >
                            <div
                                v-if="datetime.isCurrentTime"
                                :style="minuteLineStyle"
                                v-b-tooltip.hover.left="currentLineTooltip"
                            >
                                <!-- This div represents the line -->
                            </div>
                            <TreatmentPatientTimelinedDetails
                                :id="`${activity.id}_${hour}`"
                                :ref="`${activity.id}_${hour}`"
                                :treatment="treatment"
                                :activity="
                                    isActivityBelongsHere(activity, hour)
                                "
                                :cellId="`${activity.id}_${hour}`"
                                :cellDate="datetime"
                                :dialogs="dialogs"
                                :parentRef="$refs.tableContainer"
                                :prepareDateTime="prepareDateTime"
                            ></TreatmentPatientTimelinedDetails>
                        </td>
                    </tr>
                </template>
                <!-- vitals section end -->
            </tbody>
        </table>
    </div>
</template>
<script>
import TreatmentPatientTimelinedDetails from "./treatment-patient-timeline-details";
import TreatmentTimelineMixin from "@/mixins/treatment-timeline-details";
import TreatmentActivityDetails from "./treatment-activity-details";
import ScheduleHelper from "../helper";
import { DateTime } from "luxon";
import { isEmpty } from "lodash";

export default {
    name: "TreatmentRowNew",
    mixins: [ScheduleHelper, TreatmentTimelineMixin],
    components: {
        TreatmentActivityDetails,
        TreatmentPatientTimelinedDetails
    },
    props: {
        treatment: {
            type: Object,
            default() {
                return {};
            }
        },
        activities: {
            type: Object,
            default() {
                return {};
            }
        },
        dialogs: {
            type: Object,
            default() {
                return {};
            }
        },
        labelOptions: {
            type: Array,
            default() {
                return [];
            }
        },
        routeOptions: {
            type: Array,
            default() {
                return [];
            }
        },
        locationOptions: {
            type: Array,
            default() {
                return [];
            }
        }
    },
    mounted() {
        this.setToCurrentTime(); // mixin @treatment-timeline-details.js
    },
    methods: {
        isActivityBelongsHere(activity, hour) {
            const cellDate = this.datetimes[hour];
            // Convert the Unix timestamp to a DateTime object (assumed UTC)
            const dateFromCellDate = cellDate.oriDate;
            const preparedActivity = this.prepareActivity(activity);
            if (isEmpty(preparedActivity)) return;

            // Convert the Unix timestamp to a DateTime object
            let createAtDateSetZone = this._toDateTime(
                preparedActivity.referTime
            );

            // Compare just the calendar date components
            const sameDate =
                createAtDateSetZone.year === dateFromCellDate.year &&
                createAtDateSetZone.month === dateFromCellDate.month &&
                createAtDateSetZone.day === dateFromCellDate.day &&
                createAtDateSetZone.hour === dateFromCellDate.hour;
            if (sameDate) {
                return preparedActivity;
            }
        },
        prepareActivity(activity) {
            let status = this.TreatmentStatus(this.treatment);

            let preparedActivity = {};
            let filteredActivity = {};
            if (status && status.status_id > this.TREATMENT_STATUS.ADMITTED) {
                filteredActivity = {
                    ...activity,
                    dueCalculated: this._calculateDueAlt(
                        DateTime.fromSeconds(this.treatment.start),
                        activity.due,
                        activity.due_type,
                        this._now()
                    )
                };
                if (
                    filteredActivity.isactive &&
                    filteredActivity.status ==
                        this.TREATMENT_ACTIVITY_STATUS.PENDING
                ) {
                    if (filteredActivity.dueCalculated.text == "Overdue") {
                        preparedActivity = {
                            ...filteredActivity,
                            generalStatus: "overdue",
                            referTime: filteredActivity.dueCalculated.time
                        };
                    } else if (
                        filteredActivity.dueCalculated.text != "Overdue"
                    ) {
                        preparedActivity = {
                            ...filteredActivity,
                            generalStatus: "active",
                            referTime: filteredActivity.dueCalculated.time
                        };
                    }
                }
            }

            filteredActivity = {
                ...activity,
                dueCalculated: this._calculateDueAlt(
                    this._fromDateCarbon(
                        this.treatment.treatment_status[0].create_at
                    ),
                    activity.due,
                    activity.due_type,
                    this._now()
                )
            };
            if (
                filteredActivity.isactive &&
                (status.status_id > this.TREATMENT_STATUS.ADMITTED
                    ? filteredActivity.status ===
                          this.TREATMENT_ACTIVITY_STATUS.STARTED ||
                      filteredActivity.status ===
                          this.TREATMENT_ACTIVITY_STATUS.DEACTIVATED
                    : true)
            ) {
                if (filteredActivity.complete_at) {
                    preparedActivity = {
                        ...filteredActivity,
                        generalStatus: "completed",
                        referTime: filteredActivity.complete_at
                    };
                } else {
                    let referTime = this._fromDateCarbon(
                        filteredActivity.update_at
                    ).setZone(this._timeZone);
                    referTime = referTime.toSeconds();
                    return {
                        ...filteredActivity,
                        generalStatus: "completed",
                        referTime: referTime
                    };
                }
            }
            if (!isEmpty(preparedActivity)) {
                return preparedActivity;
            }
        }
    }
};
</script>

<style scoped>
.table-container {
    /* Dynamically adjust the height relative to the viewport */
    height: calc(100vh - 280px); /* adjust offset as needed */
    min-height: 400px; /* enforces min-height requirement */
    overflow-y: auto; /* allows vertical scroll */
}

/* Make the header row sticky */
thead > tr {
    position: sticky;
    top: 0;
    z-index: 4; /* higher z-index so it stays above table body rows */
    background-color: #fbfbfb; /* match your table header color */
}

/* If using table-bordered and want to retain other borders */
.no-header-border.table-bordered thead th {
    border-top: 0; /* Removes the top border */
    border-left: 0; /* Removes the left border */
    border-right: 0; /* Removes the right border */
}
.invi-border-row {
    height: 30px !important;
}
.invi-border-row td {
    border: 1px solid rgba(0, 0, 0, 0); /* Black border with 0 opacity, to avoid messing with the current line */
}
.fixed-table {
    table-layout: fixed;
    width: 100%;
}

.fixed-col-150 {
    width: 150px;
    min-width: 150px;
}

.fixed-col-70 {
    width: 70px;
    min-width: 70px;
}

/* Set fixed row heights */
.fixed-row {
    height: 70px; /* Adjust as needed */
    overflow: hidden;
}

/* Ensure cell content fits within fixed height */
.fixed-row td,
.fixed-row th {
    padding: 10px; /* Adjust padding to fit content */
    vertical-align: middle;
}

.table {
    border-collapse: separate;
    border-spacing: 0;
}
.height-one {
    height: 1px;
    position: relative;
}
.border-right-none {
    border-right: none;
}
</style>
